/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useQueryClient} from 'react-query';
import {makeStyles} from '@material-ui/core';
import isEqual from 'lodash.isequal';
import {translate} from '@ion/core/src/locale/locale';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {createSFTPServer} from '@ion/core/src/services/Reports';
import {AccountLink, SFTPConnection} from '@ion/core/src/types/entities';
import {safeParseNumber} from '@ion/core/src/utils/NumberUtil';
import QueryKey from '@ion/query/src/config/QueryKeys';
import {FormItem} from '../components/forms/FormItem';
import {FormNumberField} from '../components/forms/FormNumberField';
import {FormSection} from '../components/forms/FormSection';
import {FormTextField} from '../components/forms/FormTextField';
import {useFormSave} from '../hooks/useFormSave';
import {useFormState} from '../hooks/useFormState';
import {IonSnackbar} from '../notifications/IonSnackbar';
import {CancelButton} from './controls/CancelButton';
import {SaveButton} from './controls/SaveButton';
import AccountsChooser from './AccountsChooser';
import SFTPConfigurationChooser from './SFTPConfigurationChooser';
import {deserialize, serializer, SFTPConfigurationFields} from './SFTPConfigurationEditorUtils';

export interface SFTPEditorEnablement {
  includeSFTPEditor?: boolean;
};

export interface SFTPConfigurationProps {
  account: AccountLink;
  isView?: boolean;
};

/**
 * This interface defines the props for an SFTP Configuration Editor component.
 * It includes the associated account (`account`), an optional callback for handling
 * changes (`onChange`), a default value (`defaultValue`), and a flag to determine
 * whether to include a form (`includeForm`).
 */
export interface SFTPConfigurationEditorProps extends SFTPConfigurationProps{
  onChange?: Function;
  defaultValue?: string;
  includeForm?: boolean;
  showSSOConfig?: boolean;
};

/**
 * This interface defines the props for an SFTP Configuration Form component.
 * It includes the SFTP connection configuration to be displayed (`configuration`)
 * and the associated account (`account`).
 */
export interface SFTPConfigurationFormProps extends SFTPConfigurationProps{
  configuration: SFTPConnection;
  omitSubAccountsControl?: boolean;
  showSSOConfig?: boolean;
};

// defines the style defintion for SFTPConfigurationForm component ...
const useFormStyle = makeStyles(() => ({
  formSection : {
    paddingTop : '0px !important'
  },
  root : {
    display       : 'flex',
    flexDirection : 'column'
  },
  actions : {
    display    : 'flex',
    alignSelf  : 'flex-end',
    alignItems : 'center'
  },
  saveButtonContainer : {
    margin : '0px !important',
    width  : '122px'
  }
}));

/**
 * A component for displaying and editing SFTP connection configurations.
 *
 * ```typescript
 *
 *  import {SFTPConfigurationForm} from '@ion/dls/src/editors/';
 *
 *   <SFTPConfigurationForm account={account} configuration={configuration} />
 *
 * ```
 * @param configuration - The initial SFTP connection configuration.
 * @param account - The associated account link for the configuration.
 */
export const SFTPConfigurationForm = ({
  configuration,
  account,
  isView = false,
  showSSOConfig = false,
  omitSubAccountsControl = true
}: SFTPConfigurationFormProps) => {
  const queryClient                   = useQueryClient();
  const classes                       = useFormStyle();
  const [disableSave, setDisableSave] = useState(false);
  const [disabled, setDisabled]       = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [message, setMessage]         = useState(null);
  const [formState, setFormState]     = useFormState(deserialize(configuration || {} as SFTPConnection));
  const isDisabled                    = isView || disabled;

  const refreshSFTPConfigurations = useCallback(() => {
    const accountId = entityId(account);
    queryClient.invalidateQueries([QueryKey.GET_SFTP_CONFIGURATIONS, accountId]);
  }, [account]);

  const onCancel = useCallback((resetForm = true) => {
    setDisabled(!!configuration);
    if (resetForm) {
      setFormState(deserialize(configuration || {} as SFTPConnection));
    }
  }, [configuration]);

  const onSubmit = useCallback(async(config) => {
    try {
      setMessage(null);
      setSaveLoading(true);
      const accountId = entityId(account);
      await createSFTPServer(accountId, config);
      refreshSFTPConfigurations();
      onCancel(config?.[SFTPConfigurationFields.Name] !== configuration?.[SFTPConfigurationFields.Name]);
      setMessage(translate('admin-groups:sftpEditor.form.edit.success'));
    } catch (e) {
      setMessage(translate('admin-groups:sftpEditor.form.edit.error'));
    } finally {
      setSaveLoading(false);
    }
  }, [refreshSFTPConfigurations, configuration, onCancel]);

  const submitValidation = useMemo(() => ({
    [SFTPConfigurationFields.Password] : ({value}) => {
      return formState[SFTPConfigurationFields.RSAPrivateKey].value || value;
    },
    [SFTPConfigurationFields.RSAPrivateKey] : ({value}) => {
      return formState[SFTPConfigurationFields.Password].value || value;
    }
  }), [formState]);

  const isActiveForm = useMemo(() => (
    configuration ? !disabled : !disableSave
  ), [configuration, disabled, disableSave]);

  const handleSubmit = useFormSave({
    validate  : submitValidation,
    serialize : serializer,
    onSave    : onSubmit,
    formState,
    setFormState
  });

  useEffect(() => {
    setFormState(deserialize(configuration || {} as SFTPConnection));
    setDisabled(!!configuration);
  }, [configuration]);

  useEffect(() => {
    const configValues = Object.fromEntries(
      Object.entries(deserialize(configuration || {} as SFTPConnection)).map(([key, val]) => [key, val?.value])
    );
    const formValues = Object.fromEntries(
      Object.entries(formState).map(([key, val]) => [key, val?.value])
    );
    setDisableSave(isEqual(configValues, formValues));
  }, [formState, configuration]);

  const onFieldChange = useCallback(({target: {value, name}}) => {
    const serilizedValue = name === SFTPConfigurationFields.Port ? safeParseNumber(value, '') : value;
    setFormState({
      [name] : {
        value    : serilizedValue,
        error    : false,
        required : formState[name].required
      },
      ...(name === SFTPConfigurationFields.Password && ({
        [SFTPConfigurationFields.RSAPrivateKey] : {
          ...formState[SFTPConfigurationFields.RSAPrivateKey],
          error : false
        }
      })),
      ...(name === SFTPConfigurationFields.RSAPrivateKey && ({
        [SFTPConfigurationFields.Password] : {
          ...formState[SFTPConfigurationFields.Password],
          error : false
        }
      }))
    });
  }, [formState]);

  const onSubAccountChange = useCallback((value) => {
    setFormState({
      [SFTPConfigurationFields.AllowedAccounts] : {
        error : false,
        value
      }
    });
  }, []);

  const onEditCTAClick = useCallback(() => {
    setDisabled(false);
  }, []);

  // unset the message from state when the snakbar is closed...
  const onMessageClose = useCallback(function() {
    setMessage(null);
  }, []);

  return (
    <div className={classes.root}>
      <FormSection title={translate('admin-groups:sftpEditor.form.title')}
        overrideClasses={{formSection: classes.formSection}}
        bottomBorder={showSSOConfig}
        ctaText={disabled && configuration && !isView && translate('admin-groups:sftpEditor.form.ctaText')}
        onCTAClick={onEditCTAClick}>
        <FormItem>
          <FormTextField fieldLabel={translate('admin-groups:sftpEditor.form.serverName.fieldLabel')}
            helperText={!disabled && translate('admin-groups:sftpEditor.form.serverName.helperText')}
            value={formState[SFTPConfigurationFields.Name].value}
            hasError={formState[SFTPConfigurationFields.Name].error}
            errorText={translate('common:editors.shared.required')}
            name={SFTPConfigurationFields.Name}
            onChange={onFieldChange}
            required={isActiveForm}
            disabled={isDisabled} />
        </FormItem>
        <FormItem>
          <FormTextField fieldLabel={translate('admin-groups:sftpEditor.form.host.fieldLabel')}
            value={formState[SFTPConfigurationFields.Host].value}
            hasError={formState[SFTPConfigurationFields.Host].error}
            errorText={translate('common:editors.shared.required')}
            name={SFTPConfigurationFields.Host}
            onChange={onFieldChange}
            required={isActiveForm}
            disabled={isDisabled} />
        </FormItem>
        <FormItem>
          <FormNumberField fieldLabel={translate('admin-groups:sftpEditor.form.port.fieldLabel')}
            value={formState[SFTPConfigurationFields.Port].value}
            hasError={formState[SFTPConfigurationFields.Port].error}
            errorText={translate('common:editors.shared.required')}
            name={SFTPConfigurationFields.Port}
            onChange={onFieldChange}
            thousandSeparator={false}
            required={isActiveForm}
            disabled={isDisabled}
            max={65535}
            min={1}
            allowedRange />
        </FormItem>
        <FormItem>
          <FormTextField name={SFTPConfigurationFields.Username}
            fieldLabel={translate('admin-groups:sftpEditor.form.username.fieldLabel')}
            value={formState[SFTPConfigurationFields.Username].value}
            hasError={formState[SFTPConfigurationFields.Username].error}
            errorText={translate('common:editors.shared.required')}
            required={isActiveForm}
            onChange={onFieldChange}
            disabled={isDisabled} />
        </FormItem>
        <FormItem>
          <FormTextField fieldLabel={translate('admin-groups:sftpEditor.form.password.fieldLabel')}
            value={formState[SFTPConfigurationFields.Password].value}
            hasError={formState[SFTPConfigurationFields.Password].error}
            errorText={translate('admin-groups:sftpEditor.form.password.errorText')}
            name={SFTPConfigurationFields.Password}
            onChange={onFieldChange}
            disabled={isDisabled} />
        </FormItem>
        <FormItem>
          <FormTextField fieldLabel={translate('admin-groups:sftpEditor.form.privateKey.fieldLabel')}
            value={formState[SFTPConfigurationFields.RSAPrivateKey].value}
            hasError={formState[SFTPConfigurationFields.RSAPrivateKey].error}
            errorText={translate('admin-groups:sftpEditor.form.privateKey.errorText')}
            name={SFTPConfigurationFields.RSAPrivateKey}
            onChange={onFieldChange}
            disabled={isDisabled} />
        </FormItem>
        {!omitSubAccountsControl && (
          <FormItem>
            <AccountsChooser account={account}
              value={formState[SFTPConfigurationFields.AllowedAccounts].value}
              defaultValue={formState[SFTPConfigurationFields.AllowedAccounts].value}
              hasError={formState[SFTPConfigurationFields.AllowedAccounts].error}
              onChange={onSubAccountChange}
              disabled={isDisabled} />
          </FormItem>
        )}
      </FormSection>
      {isActiveForm && (
        <div className={classes.actions}>
          <CancelButton onCancel={onCancel} disabled={saveLoading} />
          <SaveButton label={translate('common:editors.shared.save')}
            disabled={disableSave || saveLoading}
            loading={saveLoading}
            overrideClasses={{
              container : classes.saveButtonContainer
            }}
            onSave={handleSubmit} />
        </div>
      )}
      {message && (
        <IonSnackbar message={message}
          actionLabel={translate('common:ok')}
          autoHideDuration={3000}
          onAction={onMessageClose}
          onClose={onMessageClose}
          show/>
      )}
    </div>
  );
};

/**
 * A component for selecting and editing SFTP connection configurations from any editor.
 *
 * ```typescript
 *
 *  import {SFTPConfigurationEditor} from '@ion/dls/src/editors/';
 *
 *   <SFTPConfigurationEditor account={account} onChange={onChange}
 *     defaultValue={defaultValue} />
 *
 * ```
 *
 * @param account - The associated account link for the configuration.
 * @param onChange - A callback function triggered when the configuration changes.
 * @param defaultValue - The default value to be selected initially.
 * @param includeForm - Determines whether to include the configuration form.
 */
export const SFTPConfigurationEditor = ({
  account,
  onChange,
  defaultValue,
  isView = false,
  includeForm = true,
  showSSOConfig = false
}: SFTPConfigurationEditorProps) => {
  const [selected, setSelected] = useState<any>(defaultValue);

  const accountId = useMemo(() => entityId(account), [account]);

  const onConfigurationChange = useCallback((configuration) => {
    setSelected(configuration);
    onChange?.(configuration);
  }, [onChange]);

  return (
    <>
      <FormSection title={translate('admin-groups:sftpEditor.editor.title')}
        description={translate('admin-groups:sftpEditor.editor.description')} bottomBorder={showSSOConfig}>
        <FormItem fullWidth>
          <SFTPConfigurationChooser accountId={accountId}
            onChange={onConfigurationChange}
            isView={isView}
            value={selected}
            account={account}
            defaultValue={defaultValue} />
        </FormItem>
      </FormSection>
      {includeForm && (
        <SFTPConfigurationForm configuration={selected}
          showSSOConfig={showSSOConfig}
          isView={isView}
          account={account} />
      )}
    </>
  );
};

export default SFTPConfigurationEditor;
